import { addToast, Badge, ErrorToast } from '@otter-ai/den-components';
import { useTranslations } from 'next-intl';
import type { GroupByMeetingOtid } from '../../hooks/slack/useGetUserSlackSharingConfigs';
import { trackSlack } from '../../tracking/slack-tracking';
import { AppRowSettingDetails } from '../AppRowSettingDetails';

const DATA_TEST_ID = 'slack-app-row-open-slack-modal';

export function ConnectMeetingsToSlackCta({
  isConnectedToSlack,
  isCalendarConnected,
  openSlackModal,
  userMeetingSlackSharingConfigs,
  isLoadingSlackSharingConfigs,
  openSlackWorkspaceInstallation,
  isLoadingSlackSettings,
  isWorkspaceInstallationCompleted,
}: {
  isConnectedToSlack: boolean;
  isCalendarConnected: boolean;
  openSlackModal: () => void;
  userMeetingSlackSharingConfigs: GroupByMeetingOtid;
  isLoadingSlackSharingConfigs: boolean;
  openSlackWorkspaceInstallation: () => boolean;
  isLoadingSlackSettings: boolean;
  isWorkspaceInstallationCompleted: boolean | null;
}) {
  const t = useTranslations('settings:integrations.apps.slack.extendContent');
  if (
    !isConnectedToSlack ||
    isLoadingSlackSharingConfigs ||
    isLoadingSlackSettings
  ) {
    return null;
  }

  if (!isWorkspaceInstallationCompleted) {
    return (
      <AppRowSettingDetails
        data-testid={DATA_TEST_ID}
        title={t('addOtterAppToSlackWorkspace')}
        description={t('installOtterAppToSlackWorkspace')}
        actionText={t('install')}
        onClick={() => {
          trackSlack.main.slackWorkspaceInstallationStart();
          if (!openSlackWorkspaceInstallation()) {
            addToast(
              <ErrorToast description={t('failedToOpenSlackInstallation')} />,
            );
          }
        }}
        isError
      />
    );
  }

  if (!isCalendarConnected) {
    return (
      <AppRowSettingDetails
        data-testid={DATA_TEST_ID}
        title={t('syncYourCalendar')}
        description={t('connectYourCalendar')}
        actionText={t('syncButton')}
        onClick={() => {
          trackSlack.main.slackOnboardingStart();
          openSlackModal();
        }}
      />
    );
  }

  if (userMeetingSlackSharingConfigs.size === 0) {
    return (
      <AppRowSettingDetails
        data-testid={DATA_TEST_ID}
        title={
          <div className="flex items-center gap-1">
            {t('postMeetingsToSlack')}{' '}
            <Badge color="blue" size="sm">
              {t('nextStep')}
            </Badge>
          </div>
        }
        description={t('connectMeetingsToSlack')}
        actionText={t('scheduleButton')}
        onClick={() => {
          trackSlack.main.slackOnboardingStart();
          openSlackModal();
        }}
      />
    );
  }

  return (
    <AppRowSettingDetails
      data-testid={DATA_TEST_ID}
      title={t('scheduledMeetings', {
        count: userMeetingSlackSharingConfigs.size,
      })}
      // TODO(sleahy): Show the recurring meeting names in pill tags here.
      // Get the recurring meeting names from TanStack Query's cached data when it's available.
      description={null}
      actionText={t('changeButton')}
      onClick={() => {
        trackSlack.main.slackOnboardingStart();
        openSlackModal();
      }}
    />
  );
}
